import { FC, useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgAdminPaymentMethod } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { usePgServices } from '../common/hooks';

type TMethodServicesDialogProps = {
	/** `null` — closed. */
	method: TPgAdminPaymentMethod | null;
	onClose: () => void;
	onDone: () => void;
};

/** Services a payment method may pay for (as in the customer dashboard, 03 §1.2). */
export const MethodServicesDialog: FC<TMethodServicesDialogProps> = ({
	method,
	onClose,
	onDone,
}) => {
	const { services } = usePgServices();
	const [allServices, setAllServices] = useState(true);
	const [selected, setSelected] = useState<number[]>([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!method) return;
		setAllServices(method.allServices);
		setSelected(method.services.map((service) => service.id));
		setBusy(false);
		setError(null);
	}, [method]);

	const toggle = (id: number) =>
		setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));

	const submit = async () => {
		if (!method) return;
		setBusy(true);
		setError(null);
		try {
			await api.paymentGateway.updatePaymentMethodServices({
				id: method.id,
				body: { allServices, serviceIds: allServices ? [] : selected },
			});
			notificationContainer('Services of the payment method saved', 'success');
			onDone();
		} catch (e) {
			setError(getPgErrorMessage(e, 'Failed to save the services'));
		} finally {
			setBusy(false);
		}
	};

	const activeServices = (services ?? []).filter((service) => service.isActive);

	return (
		<PgConfirmDialog
			open={!!method}
			title={`Services of ${method?.label ?? 'the payment method'}`}
			confirmLabel="Save"
			confirmDisabled={!allServices && selected.length === 0}
			busy={busy}
			error={error}
			onConfirm={() => void submit()}
			onClose={onClose}
			description="The method is charged only for the selected services."
		>
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={allServices}
							onChange={(e) => setAllServices(e.target.checked)}
							disabled={busy}
						/>
					}
					label="All services"
				/>
				{!allServices && (
					<>
						{activeServices.map((service) => (
							<FormControlLabel
								key={service.id}
								sx={{ ml: 2 }}
								control={
									<Checkbox
										checked={selected.includes(service.id)}
										onChange={() => toggle(service.id)}
										disabled={busy}
									/>
								}
								label={`${service.name} (${service.code})`}
							/>
						))}
						{!activeServices.length && (
							<Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
								No active services
							</Typography>
						)}
					</>
				)}
			</Box>
		</PgConfirmDialog>
	);
};
